// MediaForm Component
// Form for managing media uploads

import React, { useState } from 'react';
import { FaVideo, FaImage, FaFile, FaUpload, FaTrash, FaLink } from 'react-icons/fa';
import styles from './MediaForm.module.css';
import { useNotification } from '../../../context/NotificationContext';
import playerProfileService from '../../../services/playerProfile/playerProfileService';
import mediaService from '../../../services/playerProfile/mediaService';

const MediaForm = ({ media, userId, onChange }) => {
  const { showError, showSuccess, showInfo } = useNotification();
  const [activeTab, setActiveTab] = useState('videos');
  const [uploading, setUploading] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');

  // Ensure media exists
  if (!media) {
    return <div>Loading form...</div>;
  }

  // Handle file upload
  const handleFileUpload = async (e, type) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    try {
      setUploading(true);
      
      for (const file of files) {
        if (type === 'photo') {
          // Use Cloudinary for photos
          await handlePhotoUpload(file);
        } else {
          // Use old method for videos and documents
          await handleLegacyUpload(file, type);
        }
      }

      showSuccess(`${files.length} file(s) uploaded successfully`);
    } catch (err) {
      console.error('Upload error:', err);
      showError('Failed to upload files. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Handle photo upload to Cloudinary
  const handlePhotoUpload = async (file) => {
    try {
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('Photo must be less than 10MB');
      }

      // Validate file type
      if (!['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.type)) {
        throw new Error('Photo must be JPG, PNG, or WebP');
      }

      // Create FormData
      const formData = new FormData();
      formData.append('photo', file);
      // Optional metadata can be added here
      
      // Get Firebase token
      const { auth } = await import('../../../firebaseConfig');
      const token = await auth.currentUser?.getIdToken();
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000'}/api/playerProfiles/${userId}/media/photo`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          },
          body: formData
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to upload photo');
      }

      const data = await response.json();
      
      // Update local state
      const updatedMedia = { ...media };
      if (!updatedMedia.photos) {
        updatedMedia.photos = [];
      }
      updatedMedia.photos.push(data.photo);
      onChange(updatedMedia);
      
    } catch (err) {
      showError(`${file.name}: ${err.message}`);
      throw err;
    }
  };

  // Legacy upload for videos and documents
  const handleLegacyUpload = async (file, type) => {
    // TODO: Backend needs to implement video and document upload endpoints
    // For now, only photos are supported via Cloudinary
    showError(`${type === 'video' ? 'Video' : 'Document'} uploads are coming soon! Currently only photo uploads are supported.`);
    throw new Error('Feature not yet implemented');
  };

  // Handle video URL add
  const handleVideoUrlAdd = () => {
    if (!videoUrl.trim()) {
      showInfo('Please enter a video URL');
      return;
    }

    try {
      const videoInfo = mediaService.parseVideoUrl(videoUrl);
      
      if (!videoInfo) {
        showError('Invalid video URL. Please use YouTube or Vimeo links.');
        return;
      }

      const updatedMedia = { ...media };
      if (!updatedMedia.highlightVideos) {
        updatedMedia.highlightVideos = [];
      }

      updatedMedia.highlightVideos.push({
        id: Date.now().toString(),
        type: 'video',
        source: 'external',
        url: videoUrl,
        platform: videoInfo.platform,
        videoId: videoInfo.id,
        thumbnail: videoInfo.thumbnail,
        title: 'Highlight Video',
        uploadedAt: new Date().toISOString()
      });

      onChange(updatedMedia);
      setVideoUrl('');
      showSuccess('Video added successfully');
    } catch (err) {
      showError('Failed to add video URL');
    }
  };

  // Handle media delete
  const handleDelete = async (type, itemId) => {
    try {
      await playerProfileService.deleteMedia(userId, itemId);

      const updatedMedia = { ...media };
      const categoryKey = type === 'video' ? 'highlightVideos' : 
                         type === 'photo' ? 'photos' : 'documents';
      
      updatedMedia[categoryKey] = updatedMedia[categoryKey].filter(
        item => item.id !== itemId
      );
      
      onChange(updatedMedia);
      showSuccess('Media removed successfully');
    } catch (err) {
      showError('Failed to remove media');
    }
  };

  // Render media item
  const renderMediaItem = (item, type) => {
    return (
      <div key={item.id} className={styles.mediaItem}>
        {type === 'video' && (
          <div className={styles.videoThumbnail}>
            {item.thumbnail ? (
              <img src={item.thumbnail} alt={item.title} />
            ) : (
              <FaVideo />
            )}
          </div>
        )}
        
        {type === 'photo' && (
          <img 
            src={item?.cloudinary?.secureUrl || item?.cloudinary?.url || item.url || item.thumbnail} 
            alt={item.title}
            className={styles.photoThumbnail}
          />
        )}
        
        {type === 'document' && (
          <div className={styles.documentIcon}>
            <FaFile />
          </div>
        )}
        
        <div className={styles.mediaInfo}>
          <p className={styles.mediaTitle}>{item.title || 'Untitled'}</p>
          {item.size && (
            <p className={styles.mediaSize}>{mediaService.formatFileSize(item.size)}</p>
          )}
        </div>
        
        <button
          className={styles.deleteButton}
          onClick={() => handleDelete(type, item.id)}
          title="Remove"
        >
          <FaTrash />
        </button>
      </div>
    );
  };

  return (
    <div className={styles.formSection}>
      <h2 className={styles.sectionTitle}>Media Gallery</h2>
      
      <p className={styles.description}>
        Upload videos, photos, and documents to showcase your skills and achievements.
      </p>

      {/* Tab Navigation */}
      <div className={styles.tabs}>
        <button
          className={`${styles.tab} ${activeTab === 'videos' ? styles.active : ''}`}
          onClick={() => setActiveTab('videos')}
        >
          <FaVideo /> Videos ({media.highlightVideos?.length || 0})
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'photos' ? styles.active : ''}`}
          onClick={() => setActiveTab('photos')}
        >
          <FaImage /> Photos ({media.photos?.length || 0})
        </button>
        <button
          className={`${styles.tab} ${activeTab === 'documents' ? styles.active : ''}`}
          onClick={() => setActiveTab('documents')}
        >
          <FaFile /> Documents ({media.documents?.length || 0})
        </button>
      </div>

      {/* Videos Tab */}
      {activeTab === 'videos' && (
        <div className={styles.tabContent}>
          {/* Video URL Input */}
          <div className={styles.urlInput}>
            <input
              type="text"
              className={styles.input}
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              placeholder="Add YouTube or Vimeo video URL..."
              onKeyPress={(e) => e.key === 'Enter' && handleVideoUrlAdd()}
            />
            <button
              className={styles.addUrlButton}
              onClick={handleVideoUrlAdd}
            >
              <FaLink /> Add URL
            </button>
          </div>

          {/* Upload Button - Disabled (coming soon) */}
          <div className={styles.comingSoonNote}>
            <FaUpload /> Video file uploads coming soon! Use video URLs for now.
          </div>

          {/* Video List */}
          <div className={styles.mediaGrid}>
            {media.highlightVideos?.map(video => renderMediaItem(video, 'video'))}
          </div>

          {(!media.highlightVideos || media.highlightVideos.length === 0) && (
            <div className={styles.emptyState}>
              <FaVideo />
              <p>No videos uploaded yet</p>
              <p>Add YouTube/Vimeo links or upload video files</p>
            </div>
          )}
        </div>
      )}

      {/* Photos Tab */}
      {activeTab === 'photos' && (
        <div className={styles.tabContent}>
          {/* Upload Button */}
          <label className={styles.uploadButton}>
            <FaUpload /> Upload Photos
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={(e) => handleFileUpload(e, 'photo')}
              disabled={uploading}
              style={{ display: 'none' }}
            />
          </label>

          {/* Photo Grid */}
          <div className={styles.mediaGrid}>
            {media.photos?.map(photo => renderMediaItem(photo, 'photo'))}
          </div>

          {(!media.photos || media.photos.length === 0) && (
            <div className={styles.emptyState}>
              <FaImage />
              <p>No photos uploaded yet</p>
              <p>Upload images to showcase your achievements</p>
            </div>
          )}
        </div>
      )}

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <div className={styles.tabContent}>
          {/* Upload Button - Disabled (coming soon) */}
          <div className={styles.comingSoonNote}>
            <FaUpload /> Document uploads coming soon!
          </div>

          {/* Document List */}
          <div className={styles.documentList}>
            {media.documents?.map(doc => renderMediaItem(doc, 'document'))}
          </div>

          {(!media.documents || media.documents.length === 0) && (
            <div className={styles.emptyState}>
              <FaFile />
              <p>No documents uploaded yet</p>
              <p>Upload certificates, recommendations, or other documents</p>
            </div>
          )}
        </div>
      )}

      {/* Upload Progress */}
      {uploading && (
        <div className={styles.uploadProgress}>
          <div className={styles.progressBar}>
            <div className={styles.progressFill}></div>
          </div>
          <p>Uploading files...</p>
        </div>
      )}

      {/* File Size Info */}
      <div className={styles.fileInfo}>
        <p>Maximum file sizes: Videos - 500MB, Photos - 10MB, Documents - 20MB</p>
        <p>Supported formats: MP4, MOV (videos) | JPG, PNG, GIF (photos) | PDF, DOC, DOCX (documents)</p>
      </div>
    </div>
  );
};

export default MediaForm;
